// import { makeObservable, computed, action, runInAction } from "mobx";
// import firebaseAgent from "../api/firebaseagent";
// import { CartItem } from "../models/cartItem";
// import { Product } from "../models/product";
// import { BaseStore } from "./baseStore";

// export default class CartStore extends BaseStore<CartItem> {
//     private LOCAL_STORAGE_KEY = "guest_cart";

//     constructor() {
//         super();
//         makeObservable(this, {
//             total: computed,
//             itemCount: computed,
//             syncCart: action,
//             addToCart: action,
//             clearCart: action
//         });
//     }

//     protected getService() {
//         return firebaseAgent.cartItemService;
//     }

//     /**
//      * Logic: If user logs in, merge local guest items into Firestore.
//      * If guest, load from LocalStorage.
//      */
//     syncCart = async (userId?: string) => {
//         this.setItemload(false);
//         const localData = localStorage.getItem(this.LOCAL_STORAGE_KEY);
//         const localItems: CartItem[] = localData ? JSON.parse(localData) : [];

//         if (userId) {
//             // 1. Fetch Remote Cart
//             const snapshot = await this.getService().getByUser(userId);
//             const dbItems = snapshot.docs.map((doc: any) => ({ ...doc.data(), id: doc.id }));

//             // 2. Merge Guest items to Firestore if they exist
//             if (localItems.length > 0) {
//                 for (const item of localItems) {
//                     await this.addToCart(userId, item);
//                 }
//                 localStorage.removeItem(this.LOCAL_STORAGE_KEY);
//                 // Re-fetch final merged state
//                 const mergedSnapshot = await this.getService().getByUser(userId);
//                 runInAction(() => {
//                     this.items = mergedSnapshot.docs.map((doc: any) => ({ ...doc.data(), id: doc.id }));
//                 });
//             } else {
//                 runInAction(() => { this.items = dbItems; });
//             }
//         } else {
//             // Guest Mode: Load from LocalStorage
//             runInAction(() => { this.items = localItems; });
//         }
//         this.setItemload(true);
//     };

//     /**
//      * Handles Add logic for both DB and LocalStorage
//      */
//     addToCart = async (userId: string | undefined, product: Product) => {
//         if (userId) {
//             const existing = this.items.find(i => i.id === product.id);
//             if (existing) {
//                 await this.manage(existing.id!, { quantity: existing.quantity + 1 } as any);
//             } else {
//                 await this.register({ ...product, userId, quantity: 1 } as any);
//             }
//         } else {
//             const updated = [...this.items];
//             const idx = updated.findIndex(i => i.id === product.id);
//             idx > -1 ? (updated[idx].quantity += 1) : updated.push({ ...product, quantity: 1 } as any);
            
//             localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(updated));
//             runInAction(() => { this.items = updated; });
//         }
//     };

//     /**
//      * Updates quantity for the specific item
//      */
//     updateQty = async (id: string, delta: number, userId?: string) => {
//         if (userId) {
//             const item = this.items.find(i => i.id === id);
//             if (!item) return;
//             const newQty = item.quantity + delta;
//             newQty <= 0 ? await this.delete(id) : await this.manage(id, { quantity: newQty } as any);
//         } else {
//             const updated = this.items.map(i => i.id === id ? { ...i, quantity: i.quantity + delta } : i)
//                 .filter(i => i.quantity > 0);
//             localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(updated));
//             runInAction(() => { this.items = updated; });
//         }
//     };

//     /**
//      * Clears everything after a successful Booking
//      */
//     clearCart = async (userId: string | undefined) => {
//         try {
//             if (userId) {
//                 // Delete each item from Firestore sub-collection
//                 const deletePromises = this.items.map(item => this.delete(item.id!));
//                 await Promise.all(deletePromises);
//             }
            
//             // Wipe local state and storage
//             runInAction(() => {
//                 this.items = [];
//                 localStorage.removeItem(this.LOCAL_STORAGE_KEY);
//             });
//         } catch (error) {
//             console.error("Cart clear failed", error);
//         }
//     };

//     get total() {
//         return this.items.reduce((sum, item) => sum + (item.productPrice * item.quantity), 0);
//     }

//     get itemCount() {
//         return this.items.reduce((sum, item) => sum + item.quantity, 0);
//     }
// }
import { makeObservable, computed, action, runInAction } from "mobx";
import firebaseAgent from "../api/firebaseagent";
import { CartItem } from "../models/cartItem";
import { Product } from "../models/product";
import { BaseStore } from "./baseStore";
import { RootStore } from "./rootStore";

export default class CartStore extends BaseStore<CartItem> {
    private LOCAL_STORAGE_KEY = "guest_cart";    
    constructor(rootStore: RootStore) {
        super(rootStore);
        this.rootStore = rootStore;
        makeObservable(this, {
            total: computed,
            itemCount: computed,
            syncCart: action,
            addToCart: action,
            clearCart: action,
            updateQty: action
        });
    }

    protected getService() {
        return firebaseAgent.cartItemService;
    }

    syncCart = async (userId?: string) => {
        this.setItemload(false);
        try {
            const localData = localStorage.getItem(this.LOCAL_STORAGE_KEY);
            const localItems: CartItem[] = localData ? JSON.parse(localData) : [];

            if (userId) {
                // 1. Move guest items to Firestore if they exist
                if (localItems.length > 0) {
                    for (const item of localItems) {
                        await this.mergeItemToDb(userId, item);
                    }
                    localStorage.removeItem(this.LOCAL_STORAGE_KEY);
                }

                // 2. Fetch final merged state from Firestore
                const snapshot = await this.getService().getByUser(userId);
                const dbItems = snapshot.docs.map((doc: any) => ({ ...doc.data(), id: doc.id }));
                runInAction(() => { this.items = dbItems; });
            } else {
                // Guest Mode: Load from LocalStorage
                runInAction(() => { this.items = localItems; });
            }
        } catch (error) {
            console.error("Cart sync error:", error);
        } finally {
            this.setItemload(true);
        }
    };

    /**
     * Private helper to merge specific guest items into the DB
     */
    private mergeItemToDb = async (userId: string, item: CartItem) => {
        const snapshot = await this.getService().getByUser(userId);
        const existingDoc = snapshot.docs.find(d => d.data().id === item.id);

        if (existingDoc) {
            const newQty = existingDoc.data().quantity + item.quantity;
            await this.manage(existingDoc.id, { quantity: newQty } as any);
        } else {
            await this.register({ ...item, userId } as any);
        }
    };

    addToCart = async (userId: string | undefined, product: Product) => {
        if (userId) {
            const existing = this.items.find(i => i.id === product.id);
            if (existing) {
                await this.manage(existing.id!, { quantity: existing.quantity + 1 } as any);
            } else {
                await this.register({ ...product, userId, quantity: 1 } as any);
            }
            // Refresh items after DB update
            const snapshot = await this.getService().getByUser(userId);
            runInAction(() => { this.items = snapshot.docs.map((doc: any) => ({ ...doc.data(), id: doc.id })); });
        } else {
            const updated = [...this.items];
            const idx = updated.findIndex(i => i.id === product.id);
            idx > -1 ? (updated[idx].quantity += 1) : updated.push({ ...product, quantity: 1 } as any);
            localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(updated));
            runInAction(() => { this.items = updated; });
        }
    };

    updateQty = async (id: string, delta: number, userId?: string) => {
        if (userId) {
            const item = this.items.find(i => i.id === id);
            if (!item) return;
            const newQty = item.quantity + delta;
            if (newQty <= 0) {
                await this.delete(id);
            } else {
                await this.manage(id, { quantity: newQty } as any);
            }
            const snapshot = await this.getService().getByUser(userId);
            runInAction(() => { this.items = snapshot.docs.map((doc: any) => ({ ...doc.data(), id: doc.id })); });
        } else {
            const updated = this.items.map(i => i.id === id ? { ...i, quantity: i.quantity + delta } : i)
                .filter(i => i.quantity > 0);
            localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(updated));
            runInAction(() => { this.items = updated; });
        }
    };

    clearCart = async (userId: string | undefined) => {
        if (userId) {
            const deletePromises = this.items.map(item => this.delete(item.id!));
            await Promise.all(deletePromises);
        }
        runInAction(() => {
            this.items = [];
            localStorage.removeItem(this.LOCAL_STORAGE_KEY);
        });
    };

    get total() {
        return this.items.reduce((sum, item) => sum + ((item.productPrice || 0) * (item.quantity || 0)), 0);
    }

    get itemCount() {
        return this.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    }
}